import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useEffect, useState } from 'react';
import classes from './addRowModalDrugReferenceByConceptCode.module.scss';
import { checkIfStringIsValidConceptCode, formatConceptCode } from 'utils/conceptCodeUtilities';
import { toast } from 'react-toastify';

interface FormData {
  concept_code: string;
  proprietary_name: string;
  strength: number | null;
  dosage_form: string;
  coated: number | null;
  product_color?: string;
}

interface DropdownOption {
  label: string;
  value: any;
}

interface AddRowModalDrugReferenceByConceptCodeProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (formData: FormData) => Promise<void>;
  initialValues?: FormData;
  isEdit?: boolean;
  coatingOptions: DropdownOption[];
  dosageFormOptions: DropdownOption[];
}

const AddRowModalDrugReferenceByConceptCode = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit,
  coatingOptions,
  dosageFormOptions
}: AddRowModalDrugReferenceByConceptCodeProps) => {
  const [formData, setFormData] = useState<FormData>({
    concept_code: '',
    proprietary_name: '',
    strength: null,
    dosage_form: '',
    coated: null,
    product_color: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const environment = import.meta.env.VITE_ENVIRONMENT;

  useEffect(() => {
    if (initialValues && visible) {
      setFormData({ ...initialValues });
      setErrors({});
    } else if (!isEdit && visible) {
      // Reset form for new entry
      setFormData({
        concept_code: '',
        proprietary_name: '',
        strength: null,
        dosage_form: '',
        coated: null,
        product_color: ''
      });
      setErrors({});
    }
  }, [initialValues, visible, isEdit]);

  // Auto-set coating to N/A when dosage form is "Any"
  useEffect(() => {
    if (formData.dosage_form === 'Any') {
      setFormData(prev => ({ ...prev, coated: null }));
    }
  }, [formData.dosage_form]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Only validate required fields when adding new entry
    if (!isEdit) {
      // Required fields
      if (!formData.concept_code) {
        newErrors.concept_code = 'Concept Code is required';
      } else if (!checkIfStringIsValidConceptCode(formData.concept_code)) {
        newErrors.concept_code = 'Concept Code must be in format: #####-###';
      }

      if (!formData.proprietary_name) {
        newErrors.proprietary_name = 'Proprietary Name is required';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        // When editing, only submit the fields that are editable
        if (isEdit && initialValues) {
          const editedData = {
            ...initialValues,
            dosage_form: formData.dosage_form,
            coated: formData.coated,
            product_color: formData.product_color
          };
          await onSubmit(editedData);
        } else {
          // Format the concept code before submitting
          const submissionData = {
            ...formData,
            concept_code: formatConceptCode(formData.concept_code)
          };
          await onSubmit(submissionData);
        }
        // Only close modal on success
        onHide();
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error submitting form for Drug Reference by Concept Code data!');
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    // Add number-only validation for strength field
    if (field === 'strength' && value !== '') {
      // Only allow numbers
      if (!/^\d*$/.test(value)) {
        return; // Don't update if not a number
      }
    }

    // Format concept code on change
    if (field === 'concept_code' && value) {
      value = formatConceptCode(value);
    }

    // For dropdown fields, ensure we get the value, not the option object
    if (field === 'coated' && value && typeof value === 'object' && 'value' in value) {
      value = value.value;
    }

    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Check if coating should be disabled
  const isCoatingDisabled = formData.dosage_form === 'Any';

  return (
    <Dialog
      header={
        isEdit ? 'Edit Drug Reference By Concept Code' : 'Add New Drug Reference By Concept Code'
      }
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '800px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Concept Code</label>
            <InputText
              value={formData.concept_code}
              onChange={e => handleInputChange('concept_code', e.target.value)}
              placeholder="e.g. 12345-678"
              className={`${classes.inputText} ${errors.concept_code ? 'p-invalid' : ''}`}
              disabled={isEdit}
            />
            {errors.concept_code && <small className="p-error">{errors.concept_code}</small>}
          </div>

          <div className={classes.formGroup}>
            <label>Proprietary Name</label>
            <InputText
              value={formData.proprietary_name}
              onChange={e => handleInputChange('proprietary_name', e.target.value)}
              placeholder="Enter Proprietary Name"
              className={`${classes.inputText} ${errors.proprietary_name ? 'p-invalid' : ''}`}
              disabled={isEdit}
            />
            {errors.proprietary_name && (
              <small className="p-error">{errors.proprietary_name}</small>
            )}
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Strength</label>
            <InputText
              value={formData.strength?.toString() || ''}
              onChange={e =>
                handleInputChange('strength', e.target.value ? parseInt(e.target.value) : null)
              }
              placeholder="Enter Strength"
              className={classes.inputText}
              disabled={isEdit}
            />
          </div>

          <div className={classes.formGroup}>
            <label>Dosage Form</label>
            <Dropdown
              value={formData.dosage_form}
              options={dosageFormOptions}
              onChange={e => handleInputChange('dosage_form', e.value)}
              placeholder="Select Dosage Form"
              className={classes.dropdown}
              filter
            // Not disabled, as this is one of the editable fields
            />
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Coated</label>
            {isCoatingDisabled ? (
              <InputText
                value="N/A"
                className={classes.inputText}
                disabled={true}
                style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
              />
            ) : (
              <Dropdown
                value={formData.coated}
                options={coatingOptions}
                onChange={e => handleInputChange('coated', e.value)}
                placeholder="Select Coating"
                className={classes.dropdown}
                filter
                showClear
                // Not disabled, as this is one of the editable fields
                valueTemplate={option => {
                  if (option === null) return 'N/A';
                  return option.label;
                }}
              />
            )}
            {isCoatingDisabled && (
              <small className="p-text-muted">
                Coating is automatically set to N/A when Dosage Form is "Other"
              </small>
            )}
          </div>

          <div className={classes.formGroup}>
            <label>Product Color</label>
            <InputText
              value={formData.product_color}
              onChange={e => handleInputChange('product_color', e.target.value)}
              placeholder="Enter Product Color"
              className={classes.inputText}
            // Not disabled, as this is one of the editable fields
            />
          </div>
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button label="Cancel" icon="pi pi-times" onClick={onHide} severity="danger" text />

        <Button
          label="Save"
          icon="pi pi-check"
          onClick={handleSubmit}
          className={classes.saveButton}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalDrugReferenceByConceptCode;
